import { useTranslation } from 'react-i18next';
import classnames from 'classnames';

import { FP_DEFAULTS, VoteOutcome } from '~/services/freePlay/types';
import { useTransientVisible } from '~/hooks/freePlay';

import styles from './VoteResultToast.module.css';

export type VoteResultToastProps = {
    /** The settled vote to announce (shown on every gamepad). */
    outcome: VoteOutcome;
};

/* result → (variant class, icon, i18n key). 'expired' (deadline, no majority) is a
   neutral "vote ended", NOT a rejection — it shouldn't read as the room saying no. */
const VARIANTS = {
    accepted: { cls: 'accepted', icon: '✓', key: 'gamepad.freePlay.vote.resultAccepted' },
    rejected: { cls: 'rejected', icon: '✕', key: 'gamepad.freePlay.vote.resultRejected' },
    expired: { cls: 'expired', icon: '⏱', key: 'gamepad.freePlay.vote.resultExpired' },
} as const;

/**
 * Transient toast shown on EVERY gamepad once a vote settles — accepted / rejected /
 * expired — in the passenger's own language. The shared `lastVoteOutcome` persists; its
 * freshness (outcome.at) gates visibility, so a device that (re)mounts within the window
 * still catches it, and a stale outcome never shows (see useTransientVisible).
 */
export function VoteResultToast({ outcome }: VoteResultToastProps) {
    const { t } = useTranslation();
    const visible = useTransientVisible(outcome.at, FP_DEFAULTS.voteResultMs);
    if (!visible) return null;

    const variant = VARIANTS[outcome.result];
    return (
        <div className={classnames(styles.toast, styles[variant.cls])} role="status">
            <span className={styles.icon} aria-hidden>
                {variant.icon}
            </span>
            {t(variant.key)}
        </div>
    );
}
